import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Divider,
  Chip,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  MenuItem,
  Select
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import axios from 'axios';
import { usePrices } from '../hooks/usePrices';

// Helper function to check admin access
const isAdminUser = (email) => {
  return email === 'amos.shahar@gmail.com' || email === 'amos1.shahar1@gmail.com';
};

const getDefaultDateTime = () => {
  const now = new Date();
  now.setHours(now.getHours() - 5);
  return now.toISOString().slice(0, 16);
};
const toUTCISOString = (localDateString) => {
  const localDate = new Date(localDateString);
  return new Date(localDate.getTime() - localDate.getTimezoneOffset() * 60000).toISOString();
};
const fetchCurrentPrice = async (coin, provider) => {
  try {
    const params = { coin };
    if (provider) params.provider = provider;
    const response = await api.get('/api/current-price', { params });
    if (response.data && response.data.price !== undefined) {
      return { price: response.data.price, status: 'success' };
    } else if (response.data && response.data.error) {
      return { error: response.data.error, status: 'error' };
    }
    return { error: 'No price data', status: 'error' };
  } catch (err) {
    return { error: err.response?.data?.error || 'Failed to fetch current price', status: 'error' };
  }
};
function CurrentPriceCell({ coin, provider }) {
  const [state, setState] = React.useState({ loading: true, price: null, error: null });
  React.useEffect(() => {
    let mounted = true;
    setState({ loading: true, price: null, error: null });
    fetchCurrentPrice(coin, provider).then(result => {
      if (mounted) {
        setState({ loading: false, price: result.price, error: result.error });
      }
    });
    return () => { mounted = false; };
  }, [coin, provider]);
  if (state.loading) return <span>...</span>;
  if (state.error) return <span style={{ color: 'red' }}>N/A</span>;
  return <span>{Number(state.price).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: state.price < 1 ? 8 : 2 })}</span>;
}

const PROVIDER_OPTIONS = [
  'CoinGecko', 'CryptoCompare', 'Binance', 'Coinbase', 'Kraken', 'KuCoin', 'Tulidu', 'Yahoo'
];
const API_OPTIONS = [
  { label: 'Current Price', value: 'current-price' },
  { label: 'Chart Data', value: 'chart' },
  { label: 'Historical Price', value: 'historical-price' }
];

function AdminPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  // --- All hooks must be declared before any return ---
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [specificCoin, setSpecificCoin] = useState('');
  const [stats, setStats] = useState(null);
  const [exitStrategyStats, setExitStrategyStats] = useState(null);
  // --- Historical Price Data Section State and Handlers ---
  const [histCoin, setHistCoin] = useState('btc');
  const [histDatetime, setHistDatetime] = useState(getDefaultDateTime());
  const [histAllProviders, setHistAllProviders] = useState(true);
  const [histLoading, setHistLoading] = useState(false);
  const [histResults, setHistResults] = useState([]);
  const [histError, setHistError] = useState('');
  // --- Notification test state ---
  const [notifUsers, setNotifUsers] = useState([]);
  const [notifUser, setNotifUser] = useState(null);
  const [notifTypes, setNotifTypes] = useState(['web', 'fcm']);
  const [notifTitle, setNotifTitle] = useState('');
  const [notifMsg, setNotifMsg] = useState('');
  const [notifData, setNotifData] = useState('');
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifResult, setNotifResult] = useState(null);
  const [notifError, setNotifError] = useState('');
  // --- API/Provider Test Section State ---
  const [testApi, setTestApi] = useState('current-price');
  const [testProvider, setTestProvider] = useState('CoinGecko');
  const [testCoin, setTestCoin] = useState('btc');
  const [testRange, setTestRange] = useState('24h');
  const [testDatetime, setTestDatetime] = useState(getDefaultDateTime());
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [testError, setTestError] = useState('');
  // --- Direct Chart Test Section State ---
  const [chartTestCoin, setChartTestCoin] = useState('$WIF');
  const [chartTestRange, setChartTestRange] = useState('24h');
  const [chartTestPoints, setChartTestPoints] = useState(24);
  const [chartTestResult, setChartTestResult] = useState(null);
  const [chartTestLoading, setChartTestLoading] = useState(false);
  const [chartTestError, setChartTestError] = useState('');
  // Add to AdminPage state:
  const [unknownCoins, setUnknownCoins] = useState([]);
  const [loadingUnknown, setLoadingUnknown] = useState(false);
  const [editBlockchains, setEditBlockchains] = useState({});
  const [savingBlockchain, setSavingBlockchain] = useState({});

  // Price History Processor State
  const [priceHistoryStatus, setPriceHistoryStatus] = useState(null);
  const [priceHistoryLoading, setPriceHistoryLoading] = useState(false);
  const [priceHistoryError, setPriceHistoryError] = useState('');
  const [priceHistoryResult, setPriceHistoryResult] = useState('');
  const [priceHistoryStats, setPriceHistoryStats] = useState(null);
  const [detailedCoinHistory, setDetailedCoinHistory] = useState([]);
  const [detailedHistoryLoading, setDetailedHistoryLoading] = useState(false);
  const [fillIncompleteLoading, setFillIncompleteLoading] = useState(false);
  const [fillIncompleteResult, setFillIncompleteResult] = useState('');
  const [fillIncompleteProgress, setFillIncompleteProgress] = useState('');
  const [fillNullsLoading, setFillNullsLoading] = useState(false);
  const [fillNullsResult, setFillNullsResult] = useState('');
  const [fillNullsProgress, setFillNullsProgress] = useState('');

  // --- Exit Strategy Debug State ---
  const [debugXrpPrice, setDebugXrpPrice] = useState('');
  const [debugBitcoinPrice, setDebugBitcoinPrice] = useState('');
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugResult, setDebugResult] = useState(null);

  // Use direct API calls instead of usePrices hook to avoid freezing
  const [debugPrices, setDebugPrices] = useState(null);
  const [debugPricesLoading, setDebugPricesLoading] = useState(false);

  // --- All useEffect hooks must also be before any return ---
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    if (!isAdminUser(currentUser.email)) {
      navigate('/');
      return;
    }
    fetchStats();
    fetchExitStrategyStats();
    fetchPriceHistoryStatus();
    fetchPriceHistoryStats();
    fetchDetailedCoinHistory();
    
    // Fetch current prices for debug interface
    fetchCurrentPrices();
    

  }, [currentUser, navigate]);



  useEffect(() => {
    api.get('/api/admin/users')
      .then(res => setNotifUsers(res.data.users || []))
      .catch(() => setNotifUsers([]));
  }, []);

  const fetchUnknown = async () => {
    setLoadingUnknown(true);
    try {
      const resp = await api.get('/api/coins');
      setUnknownCoins((resp.data || []).filter(c => c.blockchain === 'unknown'));
    } catch {
      setUnknownCoins([]);
    }
    setLoadingUnknown(false);
  };

  useEffect(() => {
    fetchUnknown();
  }, []);

  // Fetch current prices directly from API
  const fetchCurrentPrices = async () => {
    try {
      console.log('Fetching current prices...');
      setDebugPricesLoading(true);
      console.log('Making API calls...');
      const [xrpResponse, btcResponse] = await Promise.all([
        api.get('/api/current-price', { params: { coin: 'xrp' } }),
        api.get('/api/current-price', { params: { coin: 'btc' } })
      ]);
      
      console.log('XRP full response:', xrpResponse);
      console.log('BTC full response:', btcResponse);
      console.log('XRP data:', xrpResponse.data);
      console.log('BTC data:', btcResponse.data);
      
      // Check for errors in responses
      if (xrpResponse.data?.error) {
        console.error('XRP API error:', xrpResponse.data.error);
      }
      if (btcResponse.data?.error) {
        console.error('BTC API error:', btcResponse.data.error);
      }
      
      const prices = {};
      
      // Handle the actual API response structure: { results: [{ provider, price, status }] }
      if (xrpResponse.data?.results && xrpResponse.data.results.length > 0) {
        const xrpResult = xrpResponse.data.results[0];
        if (xrpResult.status === 'success' && xrpResult.price) {
          prices.XRP = { usd: xrpResult.price };
          console.log('XRP price extracted:', xrpResult.price);
        }
      }
      
      if (btcResponse.data?.results && btcResponse.data.results.length > 0) {
        const btcResult = btcResponse.data.results[0];
        if (btcResult.status === 'success' && btcResult.price) {
          prices.BTC = { usd: btcResult.price };
          console.log('BTC price extracted:', btcResult.price);
        }
      }
      
      console.log('Setting debugPrices to:', prices);
      setDebugPrices(prices);
      console.log('Fetched current prices:', prices);
      
      // If we got prices, also set the input values
      if (prices.XRP?.usd) {
        setDebugXrpPrice(prices.XRP.usd.toString());
        console.log('Set XRP input to:', prices.XRP.usd);
      }
      if (prices.BTC?.usd) {
        setDebugBitcoinPrice(prices.BTC.usd.toString());
        console.log('Set BTC input to:', prices.BTC.usd);
      }
    } catch (err) {
      console.error('Failed to fetch current prices:', err);
      setError('Failed to fetch current prices');
      
      // Set fallback prices so you can test the functionality
      console.log('Setting fallback prices for testing');
      setDebugXrpPrice('0.50');
      setDebugBitcoinPrice('45000');
    } finally {
      setDebugPricesLoading(false);
    }
  };

  // Initialize debug prices once when prices are first loaded
  useEffect(() => {
    console.log('debugPrices changed:', debugPrices);
    // Only set prices if they haven't been set yet and prices are available
    if (debugPrices && Object.keys(debugPrices).length > 0) {
      if (debugPrices.XRP?.usd && !debugXrpPrice) {
        console.log('Setting XRP debug price:', debugPrices.XRP.usd);
        setDebugXrpPrice(debugPrices.XRP.usd.toString());
      }
      if (debugPrices.BTC?.usd && !debugBitcoinPrice) {
        console.log('Setting BTC debug price:', debugPrices.BTC.usd);
        setDebugBitcoinPrice(debugPrices.BTC.usd.toString());
      }
    }
  }, [debugPrices]); // Only depend on debugPrices

  // Fallback: Set some default prices if usePrices doesn't work
  // Temporarily disabled to test if it's causing the freeze
  /*
  useEffect(() => {
    // Only set defaults if no prices are set and we've waited a bit for usePrices to load
    if (!debugXrpPrice && !debugBitcoinPrice) {
      const timer = setTimeout(() => {
        if (!debugXrpPrice && !debugBitcoinPrice) {
          console.log('Setting fallback debug prices');
          setDebugXrpPrice('0.50');
          setDebugBitcoinPrice('45000');
        }
      }, 3000); // Wait 3 seconds for usePrices to load
      
      return () => clearTimeout(timer);
    }
  }, [debugXrpPrice, debugBitcoinPrice]);
  */



  // --- Only now, after all hooks, do conditional returns ---
  if (!currentUser) {
    return null;
  }
  if (!isAdminUser(currentUser.email)) {
    return <div>Not authorized to access admin page</div>;
  }

  const handleHistSubmit = async (e) => {
    e.preventDefault();
    if (!histCoin.trim()) {
      setHistError('Please enter a coin symbol');
      return;
    }
    if (!histDatetime) {
      setHistError('Please enter a date and time');
      return;
    }
    setHistLoading(true);
    setHistError('');
    setHistResults([]);
    try {
      const response = await api.get('/api/historical-price', {
        params: {
          coin: histCoin.trim().toLowerCase(),
          datetime: toUTCISOString(histDatetime),
          allProviders: histAllProviders
        }
      });
      setHistResults(response.data.results || []);
    } catch (err) {
      setHistError(err.response?.data?.error || 'Failed to fetch historical prices');
    } finally {
      setHistLoading(false);
    }
  };
  const formatHistPrice = (price) => {
    if (price === null || price === undefined) return 'N/A';
    return Number(price).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: price < 1 ? 8 : 2
    });
  };
  const formatHistTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };
  const getHistStatusColor = (status) => status === 'success' ? 'success' : 'error';
  const getHistAccuracyColor = (accuracy) => {
    switch (accuracy) {
      case 'minute': return 'success';
      case 'hour': return 'warning';
      case 'day': return 'info';
      default: return 'default';
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/api/coin-data-stats');
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchExitStrategyStats = async () => {
    try {
      const response = await api.get('/api/admin/exit-strategy-stats');
      setExitStrategyStats(response.data);
    } catch (err) {
      console.error('Error fetching exit strategy stats:', err);
    }
  };

  const handleRefreshAll = async () => {
    setLoading(true);
    setError('');
    setMessage('');
    
    try {
      const response = await api.post('/api/coins/data/refresh');
      const { processed, costTracking, message: responseMessage } = response.data;
      
      setMessage(`${responseMessage}. Processed: ${processed} coins. Cost: $${costTracking?.estimatedCost?.toFixed(4) || '0.0000'}`);
      
      // Refresh stats
      await fetchStats();
      
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshSpecific = async () => {
    if (!specificCoin.trim()) {
      setError('Please enter a coin symbol');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');
    
    try {
      const response = await api.post('/api/coins/data/refresh', {
        specificCoin: specificCoin.trim().toUpperCase()
      });
      
      const { processed, costTracking, message: responseMessage } = response.data;
      
      setMessage(`${responseMessage}. Processed: ${processed} coins. Cost: $${costTracking?.estimatedCost?.toFixed(4) || '0.0000'}`);
      setSpecificCoin('');
      
      // Refresh stats
      await fetchStats();
      
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendNotif = async () => {
    setNotifLoading(true);
    setNotifResult(null);
    setNotifError('');
    try {
      const res = await api.post('/api/admin/send-notification', {
        userId: notifUser?.id,
        types: notifTypes,
        title: notifTitle,
        message: notifMsg,
        data: notifData ? JSON.parse(notifData) : undefined
      });
      setNotifResult(res.data.result);
    } catch (err) {
      setNotifError(err.response?.data?.error || err.message);
    } finally {
      setNotifLoading(false);
    }
  };

  const handleApiTest = async () => {
    setTestLoading(true);
    setTestResult(null);
    setTestError('');
    try {
      let response;
      if (testApi === 'current-price') {
        response = await api.get('/api/current-price', {
          params: { coin: testCoin, provider: testProvider }
        });
      } else if (testApi === 'chart') {
        response = await api.get(`/api/coins/${testCoin}/chart`, {
          params: { range: testRange, provider: testProvider }
        });
      } else if (testApi === 'historical-price') {
        response = await api.get('/api/historical-price', {
          params: {
            coin: testCoin,
            datetime: toUTCISOString(testDatetime),
            provider: testProvider
          }
        });
      }
      setTestResult(response.data);
    } catch (err) {
      let details = '';
      if (err.response && err.response.data) {
        if (err.response.data.details) {
          details = err.response.data.details;
        } else if (typeof err.response.data === 'string') {
          details = err.response.data;
        }
      }
      setTestResult({ details });
      setTestError(err.response?.data?.error || err.message);
    } finally {
      setTestLoading(false);
    }
  };

  const handleChartTest = async () => {
    setChartTestLoading(true);
    setChartTestResult(null);
    setChartTestError('');
    try {
      // Get chart data
      const chartResponse = await api.get(`/api/coins/${chartTestCoin}/chart`, {
        params: { 
          range: chartTestRange, 
          points: chartTestPoints,
          optimize: true
        }
      });
      
      // Get current price from database (like the portfolio page does)
      let currentPrice = null;
      try {
        const coinsResponse = await api.get('/api/coins');
        const coin = coinsResponse.data.find(c => c.symbol.toUpperCase() === chartTestCoin.toUpperCase());
        if (coin && coin.current_price && parseFloat(coin.current_price) > 0) {
          currentPrice = parseFloat(coin.current_price);
        }
      } catch (priceErr) {
        console.log('Could not fetch current price from database:', priceErr.message);
      }
      
      // Combine results
      const result = {
        ...chartResponse.data,
        currentPrice: currentPrice
      };
      
      setChartTestResult(result);
    } catch (err) {
      setChartTestError(err.response?.data?.error || err.message);
    } finally {
      setChartTestLoading(false);
    }
  };

  const handleEditBlockchain = (symbol, value) => {
    setEditBlockchains(prev => ({ ...prev, [symbol]: value }));
  };
  const handleSaveBlockchain = async (coin) => {
    try {
      setSavingBlockchain(prev => ({ ...prev, [coin.symbol]: true }));
      await api.post('/api/admin/update-blockchain', {
        symbol: coin.symbol,
        blockchain: editBlockchains[coin.symbol]
      });
      setMessage(`Blockchain updated for ${coin.symbol}`);
      fetchUnknown();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update blockchain');
    } finally {
      setSavingBlockchain(prev => ({ ...prev, [coin.symbol]: false }));
    }
  };

  // Exit Strategy Debug Handlers
  const handleDebugExitStrategyCheck = async () => {
    try {
      setDebugLoading(true);
      setDebugResult(null);
      setError('');
      
      const response = await api.post('/api/exit-strategies/debug-check', {
        xrpPrice: parseFloat(debugXrpPrice),
        bitcoinPrice: parseFloat(debugBitcoinPrice)
      });
      
      setDebugResult(response.data);
      setMessage('Exit strategy debug check completed successfully');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to run debug exit strategy check');
      console.error('Debug exit strategy check error:', err);
    } finally {
      setDebugLoading(false);
    }
  };

  const handleResetDebugPrices = async () => {
    try {
      setDebugPricesLoading(true);
      
      // Fetch fresh prices from API
      const [xrpResponse, btcResponse] = await Promise.all([
        api.get('/api/current-price', { params: { coin: 'xrp' } }),
        api.get('/api/current-price', { params: { coin: 'btc' } })
      ]);
      
      // Update debug inputs directly with fresh prices
      if (xrpResponse.data?.results && xrpResponse.data.results.length > 0) {
        const xrpResult = xrpResponse.data.results[0];
        if (xrpResult.status === 'success' && xrpResult.price) {
          const xrpPrice = xrpResult.price.toString();
          setDebugXrpPrice(xrpPrice);
          console.log('Updated XRP price to:', xrpPrice);
        }
      }
      
      if (btcResponse.data?.results && btcResponse.data.results.length > 0) {
        const btcResult = btcResponse.data.results[0];
        if (btcResult.status === 'success' && btcResult.price) {
          const btcPrice = btcResult.price.toString();
          setDebugBitcoinPrice(btcPrice);
          console.log('Updated BTC price to:', btcPrice);
        }
      }
      
      // Also update the debugPrices state for consistency
      const prices = {};
      if (xrpResponse.data?.results && xrpResponse.data.results.length > 0) {
        const xrpResult = xrpResponse.data.results[0];
        if (xrpResult.status === 'success' && xrpResult.price) {
          prices.XRP = { usd: xrpResult.price };
        }
      }
      if (btcResponse.data?.results && btcResponse.data.results.length > 0) {
        const btcResult = btcResponse.data.results[0];
        if (btcResult.status === 'success' && btcResult.price) {
          prices.BTC = { usd: btcResult.price };
        }
      }
      setDebugPrices(prices);
      
      setMessage('Debug prices reset to current market prices');
    } catch (err) {
      setError('Failed to fetch current prices for reset');
      console.error('Reset debug prices error:', err);
    } finally {
      setDebugPricesLoading(false);
    }
  };

  // Price History Processor Handlers
  const fetchPriceHistoryStatus = async () => {
    try {
      setPriceHistoryLoading(true);
      setPriceHistoryError('');
      const response = await api.get('/api/portfolio/admin/price-history/status');
      setPriceHistoryStatus(response.data.status);
    } catch (err) {
      setPriceHistoryError(err.response?.data?.error || 'Failed to fetch status');
    } finally {
      setPriceHistoryLoading(false);
    }
  };

  const triggerPriceHistoryProcess = async () => {
    try {
      setPriceHistoryLoading(true);
      setPriceHistoryError('');
      setPriceHistoryResult('');
      const response = await api.post('/api/portfolio/admin/price-history/trigger');
      setPriceHistoryResult(response.data.message);
      // Refresh status and stats after triggering
      setTimeout(() => {
        fetchPriceHistoryStatus();
        fetchPriceHistoryStats();
      }, 1000);
    } catch (err) {
      setPriceHistoryError(err.response?.data?.error || 'Failed to trigger process');
    } finally {
      setPriceHistoryLoading(false);
    }
  };

  const fetchPriceHistoryStats = async () => {
    try {
      const response = await api.get('/api/portfolio/admin/price-history/stats');
      setPriceHistoryStats(response.data.stats);
    } catch (err) {
      console.error('Error fetching price history stats:', err);
    }
  };

  const fetchDetailedCoinHistory = async () => {
    try {
      setDetailedHistoryLoading(true);
      console.log('[AdminPage] Fetching detailed coin history...');
      const response = await api.get('/api/portfolio/admin/price-history/detailed');
      console.log('[AdminPage] Detailed coin history response:', response.data);
      setDetailedCoinHistory(response.data.detailedData);
    } catch (err) {
      console.error('[AdminPage] Error fetching detailed coin history:', err);
      console.error('[AdminPage] Error response:', err.response?.data);
    } finally {
      setDetailedHistoryLoading(false);
    }
  };

  const fillIncompleteHistoricalData = async () => {
    try {
      setFillIncompleteLoading(true);
      setFillIncompleteResult('');
      setFillIncompleteProgress('Starting process...');
      console.log('[AdminPage] Triggering fill incomplete historical data...');
      
      // Start polling for progress
      const pollProgress = setInterval(async () => {
        try {
          const response = await api.get('/api/portfolio/admin/price-history/status');
          if (response.data.status?.currentOperation) {
            setFillIncompleteProgress(response.data.status.currentOperation);
          }
        } catch (err) {
          // Ignore polling errors
        }
      }, 2000);
      
      const response = await api.post('/api/portfolio/admin/price-history/fill-incomplete');
      console.log('[AdminPage] Fill incomplete response:', response.data);
      setFillIncompleteResult(response.data.result.message);
      
      // Stop polling
      clearInterval(pollProgress);
      setFillIncompleteProgress('');
      
      // Refresh the detailed data after filling
      await fetchDetailedCoinHistory();
    } catch (err) {
      console.error('[AdminPage] Error filling incomplete data:', err);
      setFillIncompleteResult('Error: ' + (err.response?.data?.error || err.message));
      setFillIncompleteProgress('');
    } finally {
      setFillIncompleteLoading(false);
    }
  };

  const fillNullPrices = async () => {
    try {
      setFillNullsLoading(true);
      setFillNullsResult('');
      setFillNullsProgress('Starting NULL price filling...');
      console.log('[AdminPage] Triggering NULL price filling...');
      
      const response = await api.post('/api/portfolio/admin/price-history/fill-nulls');
      console.log('[AdminPage] Fill NULLs response:', response.data);
      setFillNullsResult(response.data.result.message);
      setFillNullsProgress('');
      
      // Refresh the detailed data after filling
      await fetchDetailedCoinHistory();
    } catch (err) {
      console.error('[AdminPage] Error in NULL price filling:', err);
      setFillNullsResult('Error: ' + (err.response?.data?.error || err.message));
      setFillNullsProgress('');
    } finally {
      setFillNullsLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
              <Typography variant="h4" component="h1" gutterBottom>
          Admin Dashboard
        </Typography>
      
      <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
        Coin Data Management
      </Typography>

      {/* Database Stats Card */}
      {stats && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Database Statistics
            </Typography>
            <Grid container spacing={2}>
              <Grid sx={{ width: '25%' }}>
                <Typography variant="body2" color="textSecondary">Total Coins</Typography>
                <Typography variant="h6">{stats.totalCoins}</Typography>
              </Grid>
              <Grid sx={{ width: '25%' }}>
                <Typography variant="body2" color="textSecondary">Coins with Data</Typography>
                <Typography variant="h6" color="success.main">{stats.coinsWithData}</Typography>
              </Grid>
              <Grid sx={{ width: '25%' }}>
                <Typography variant="body2" color="textSecondary">Coins without Data</Typography>
                <Typography variant="h6" color="warning.main">{stats.coinsWithoutData}</Typography>
              </Grid>
              <Grid sx={{ width: '25%' }}>
                <Typography variant="body2" color="textSecondary">Data Coverage</Typography>
                <Typography variant="h6">{stats.coveragePercentage}%</Typography>
              </Grid>
              <Grid sx={{ width: '25%' }}>
                <Typography variant="body2" color="textSecondary">Total Users</Typography>
                <Typography variant="h6" color="info.main">{stats.totalUsers}</Typography>
              </Grid>
              <Grid sx={{ width: '25%' }}>
                <Typography variant="body2" color="textSecondary">Total Wallets</Typography>
                <Typography variant="h6" color="primary.main">{stats.totalWallets}</Typography>
              </Grid>
              <Grid sx={{ width: '25%' }}>
                <Typography variant="body2" color="textSecondary">Total Transactions</Typography>
                <Typography variant="h6" color="secondary.main">{stats.totalTransactions}</Typography>
              </Grid>
              <Grid sx={{ width: '25%' }}>
                <Typography variant="body2" color="textSecondary">Manual Transactions</Typography>
                <Typography variant="h6" color="info.main">{stats.manualTransactions}</Typography>
              </Grid>
              <Grid sx={{ width: '25%' }}>
                <Typography variant="body2" color="textSecondary">Wallet Transactions</Typography>
                <Typography variant="h6" color="success.main">{stats.walletTransactions}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Exit Strategy Stats Card */}
      {exitStrategyStats && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Exit Strategy Statistics
            </Typography>
            <Grid container spacing={2}>
              <Grid sx={{ width: '25%' }}>
                <Typography variant="body2" color="textSecondary">Total Strategies</Typography>
                <Typography variant="h6">{exitStrategyStats.totalStrategies}</Typography>
              </Grid>
              <Grid sx={{ width: '25%' }}>
                <Typography variant="body2" color="textSecondary">Total Steps</Typography>
                <Typography variant="h6" color="primary.main">{exitStrategyStats.totalSteps}</Typography>
              </Grid>
              <Grid sx={{ width: '25%' }}>
                <Typography variant="body2" color="textSecondary">Triggered (24h)</Typography>
                <Typography variant="h6" color="warning.main">{exitStrategyStats.triggeredSteps24h}</Typography>
              </Grid>
              <Grid sx={{ width: '25%' }}>
                <Typography variant="body2" color="textSecondary">Active Users</Typography>
                <Typography variant="h6" color="success.main">{exitStrategyStats.activeUsers}</Typography>
              </Grid>
              <Grid sx={{ width: '25%' }}>
                <Typography variant="body2" color="textSecondary">Take Profit</Typography>
                <Typography variant="h6" color="success.main">{exitStrategyStats.strategiesByType.take_profit || 0}</Typography>
              </Grid>
              <Grid sx={{ width: '25%' }}>
                <Typography variant="body2" color="textSecondary">Stop Loss</Typography>
                <Typography variant="h6" color="error.main">{exitStrategyStats.strategiesByType.stop_loss || 0}</Typography>
              </Grid>
            </Grid>
            
            {/* Popular Coins */}
            {exitStrategyStats.popularCoins && exitStrategyStats.popularCoins.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Most Popular Coins
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {exitStrategyStats.popularCoins.map((coin, index) => (
                    <Chip
                      key={index}
                      label={`${coin.symbol} (${coin.strategy_count})`}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Exit Strategy Debug Interface */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Exit Strategy Debug Interface
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Test exit strategy notifications with fake prices (for debugging only)
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                label="XRP Price ($)"
                type="number"
                value={debugXrpPrice || ''}
                onChange={(e) => {
                  console.log('XRP input change:', e.target.value);
                  setDebugXrpPrice(e.target.value);
                }}
                onFocus={() => console.log('XRP input focused')}
                onBlur={() => console.log('XRP input blurred')}
                fullWidth
                size="small"
                inputProps={{ step: "0.01", min: "0" }}
                placeholder="0.50"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Bitcoin Price ($)"
                type="number"
                value={debugBitcoinPrice || ''}
                onChange={(e) => {
                  console.log('BTC input change:', e.target.value);
                  setDebugBitcoinPrice(e.target.value);
                }}
                onFocus={() => console.log('BTC input focused')}
                onBlur={() => console.log('BTC input blurred')}
                fullWidth
                size="small"
                inputProps={{ step: "100", min: "0" }}
                placeholder="45000"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                color="warning"
                onClick={handleDebugExitStrategyCheck}
                disabled={!debugXrpPrice || !debugBitcoinPrice || debugLoading}
                fullWidth
                size="small"
              >
                {debugLoading ? <CircularProgress size={20} /> : 'Test Exit Strategies'}
              </Button>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="outlined"
                onClick={handleResetDebugPrices}
                disabled={debugPricesLoading}
                fullWidth
                size="small"
              >
                {debugPricesLoading ? <CircularProgress size={20} /> : 'Reset to Current Prices'}
              </Button>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="outlined"
                onClick={() => {
                  console.log('Manual test - current debugPrices:', debugPrices);
                  console.log('Manual test - current debugXrpPrice:', debugXrpPrice);
                  console.log('Manual test - current debugBitcoinPrice:', debugBitcoinPrice);
                }}
                fullWidth
                size="small"
              >
                Debug Info
              </Button>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="outlined"
                onClick={async () => {
                  console.log('Testing API with existing function...');
                  const xrpResult = await fetchCurrentPrice('xrp');
                  const btcResult = await fetchCurrentPrice('btc');
                  console.log('XRP result:', xrpResult);
                  console.log('BTC result:', btcResult);
                }}
                fullWidth
                size="small"
              >
                Test API
              </Button>
            </Grid>
          </Grid>
          
          {/* Debug Results */}
          {debugResult && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Debug Results:
              </Typography>
              <Typography variant="body2" component="pre" sx={{ fontSize: '0.8rem' }}>
                {JSON.stringify(debugResult, null, 2)}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* --- API/Provider Test Section --- */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Provider/API Test
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <Select
                value={testApi}
                onChange={e => setTestApi(e.target.value)}
                fullWidth
                size="small"
              >
                {API_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} md={3}>
              <Select
                value={testProvider}
                onChange={e => setTestProvider(e.target.value)}
                fullWidth
                size="small"
              >
                {PROVIDER_OPTIONS.map(opt => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="Coin"
                value={testCoin}
                onChange={e => setTestCoin(e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            {testApi === 'chart' && (
              <Grid item xs={12} md={2}>
                <TextField
                  label="Range"
                  value={testRange}
                  onChange={e => setTestRange(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="24h, 1d, 5d, 1m, ..."
                />
              </Grid>
            )}
            {testApi === 'historical-price' && (
              <Grid item xs={12} md={2}>
                <TextField
                  label="Datetime"
                  type="datetime-local"
                  value={testDatetime}
                  onChange={e => setTestDatetime(e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                onClick={handleApiTest}
                disabled={testLoading}
                fullWidth
                sx={{ height: 40 }}
              >
                {testLoading ? <CircularProgress size={20} /> : 'Execute'}
              </Button>
            </Grid>
          </Grid>
          {testError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {testError}
              {testResult && testResult.details && (
                <>
                  <br />
                  <span style={{ fontSize: 13, color: '#888' }}>{testResult.details}</span>
                </>
              )}
            </Alert>
          )}
          {testResult && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Result:</Typography>
              <Paper variant="outlined" sx={{ p: 2, mt: 1, maxHeight: 300, overflow: 'auto', fontFamily: 'monospace', fontSize: 14 }}>
                <pre style={{ margin: 0 }}>{JSON.stringify(testResult, null, 2)}</pre>
              </Paper>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* --- Direct Chart Test Section --- */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Direct Chart Test (Portfolio Sparklines)
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Test the same chart API that portfolio page uses for sparklines: /api/coins/{'{coin}'}/chart
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                label="Coin Symbol"
                value={chartTestCoin}
                onChange={e => setChartTestCoin(e.target.value)}
                fullWidth
                size="small"
                placeholder="e.g., $WIF, BTC, BONK"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Range"
                value={chartTestRange}
                onChange={e => setChartTestRange(e.target.value)}
                fullWidth
                size="small"
                placeholder="24h, 1d, 5d, 1m, ..."
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="Points"
                type="number"
                value={chartTestPoints}
                onChange={e => setChartTestPoints(parseInt(e.target.value) || 24)}
                fullWidth
                size="small"
                placeholder="24"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                onClick={handleChartTest}
                disabled={chartTestLoading}
                fullWidth
                sx={{ height: 40 }}
              >
                {chartTestLoading ? <CircularProgress size={20} /> : 'Test Chart'}
              </Button>
            </Grid>
          </Grid>
          {chartTestError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {chartTestError}
            </Alert>
          )}
          {chartTestResult && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Chart Result:</Typography>
              <Paper variant="outlined" sx={{ p: 2, mt: 1, maxHeight: 400, overflow: 'auto', fontFamily: 'monospace', fontSize: 12 }}>
                <pre style={{ margin: 0 }}>{JSON.stringify(chartTestResult, null, 2)}</pre>
              </Paper>
              {chartTestResult.data && chartTestResult.data.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">Summary:</Typography>
                  <Typography variant="body2">
                    Provider: {chartTestResult.provider || 'Unknown'} | 
                    Data Points: {chartTestResult.data.length} | 
                    First Price (24h ago): ${chartTestResult.data[0]?.close?.toFixed(6)} | 
                    Last Price (chart): ${chartTestResult.data[chartTestResult.data.length - 1]?.close?.toFixed(6)} | 
                    Current Price (DB): ${chartTestResult.currentPrice?.toFixed(6) || 'N/A'} | 
                    24h Change (chart): {((chartTestResult.data[chartTestResult.data.length - 1]?.close - chartTestResult.data[0]?.close) / chartTestResult.data[0]?.close * 100).toFixed(2)}%
                  </Typography>
                  {chartTestResult.currentPrice ? (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>24h Change (vs Current):</strong> {((chartTestResult.currentPrice - chartTestResult.data[0]?.close) / chartTestResult.data[0]?.close * 100).toFixed(2)}%
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>24h Change (vs Current):</strong> N/A (no current price available)
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* --- Historical Price Data Section --- */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Historical Price Data
          </Typography>
          <form onSubmit={handleHistSubmit}>
            <Grid container spacing={3}>
              <Grid sx={{ width: '20%' }}>
                <TextField
                  fullWidth
                  label="Coin Symbol"
                  value={histCoin}
                  onChange={(e) => setHistCoin(e.target.value)}
                  placeholder="e.g., bitcoin, ethereum"
                  required
                />
              </Grid>
              <Grid sx={{ width: '20%' }}>
                <TextField
                  fullWidth
                  label="Date & Time"
                  type="datetime-local"
                  value={histDatetime}
                  onChange={(e) => setHistDatetime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid sx={{ width: '20%' }}>
                <Select
                  value={histAllProviders ? '' : (histResults[0]?.provider || PROVIDER_OPTIONS[0])}
                  onChange={e => {
                    if (e.target.value === '') setHistAllProviders(true);
                    else {
                      setHistAllProviders(false);
                      // Optionally set a specific provider for the next request
                      // (handled in handleHistSubmit)
                    }
                  }}
                  displayEmpty
                  fullWidth
                  size="small"
                >
                  <MenuItem value="">All Providers (fallback)</MenuItem>
                  {PROVIDER_OPTIONS.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid sx={{ width: '20%' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={histAllProviders}
                      onChange={(e) => setHistAllProviders(e.target.checked)}
                    />
                  }
                  label="Query All Providers"
                />
              </Grid>
              <Grid sx={{ width: '20%' }}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={histLoading}
                  sx={{ height: 56 }}
                >
                  {histLoading ? <CircularProgress size={24} /> : 'Fetch Data'}
                </Button>
              </Grid>
            </Grid>
          </form>
          {histError && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>{histError}</Alert>
          )}
          {histResults.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Results ({histResults.length})
              </Typography>
              <Paper variant="outlined">
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Provider</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Open</TableCell>
                        <TableCell align="right">High</TableCell>
                        <TableCell align="right">Low</TableCell>
                        <TableCell align="right">Close</TableCell>
                        <TableCell>Accuracy</TableCell>
                        <TableCell>Timestamp</TableCell>
                        <TableCell>Current Price</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {histResults.map((result, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {result.provider}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={result.status}
                              color={getHistStatusColor(result.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="text.secondary">
                              {formatHistPrice(result.open)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="success.main" fontWeight="medium">
                              {formatHistPrice(result.max)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="error.main" fontWeight="medium">
                              {formatHistPrice(result.min)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="medium">
                              {formatHistPrice(result.close)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={result.accuracy || 'N/A'}
                              color={getHistAccuracyColor(result.accuracy)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {formatHistTimestamp(result.timestamp)}
                            </Typography>
                          </TableCell>
                          <TableCell><CurrentPriceCell coin={histCoin} provider={result.provider} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
              {histResults.some(r => r.status === 'error') && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Some providers returned errors
                </Alert>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <Grid container spacing={3}>
        {/* Refresh All Coins */}
        <Grid sx={{ width: '50%' }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Refresh All Missing Coins
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Process up to 10 coins
            </Typography>
            <Button
              variant="contained"
              onClick={handleRefreshAll}
              disabled={loading}
              sx={{ mb: 2 }}
            >
              {loading ? <CircularProgress size={20} /> : 'Process Missing Coins'}
            </Button>
            <Typography variant="caption" color="textSecondary">
              Only processes coins not in database
            </Typography>
          </Paper>
        </Grid>

        {/* Refresh Specific Coin */}
        <Grid sx={{ width: '50%' }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Refresh Specific Coin
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Process specific coin (e.g., bitcoin, ethereum)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                size="small"
                placeholder="Enter coin symbol"
                value={specificCoin}
                onChange={(e) => setSpecificCoin(e.target.value)}
                sx={{ flexGrow: 1 }}
              />
              <Button
                variant="contained"
                onClick={handleRefreshSpecific}
                disabled={loading || !specificCoin.trim()}
              >
                {loading ? <CircularProgress size={20} /> : 'Process'}
              </Button>
            </Box>
            <Typography variant="caption" color="textSecondary">
              Only if coin has no data
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Send Test Notification Section */}
      <Box sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Send Test Notification
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={notifUsers}
                getOptionLabel={u => `${u.public_name || u.email} (${u.email})`}
                value={notifUser}
                onChange={(_, v) => setNotifUser(v)}
                renderInput={params => <TextField {...params} label="User" required fullWidth />}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                fullWidth
                sx={{ minWidth: 320, maxWidth: '100%' }}
                componentsProps={{ paper: { sx: { minWidth: 320, maxWidth: '100%' } } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={<Checkbox checked={notifTypes.includes('web')} onChange={e => setNotifTypes(t => e.target.checked ? [...t, 'web'] : t.filter(x => x !== 'web'))} />}
                label="Web"
              />
              <FormControlLabel
                control={<Checkbox checked={notifTypes.includes('fcm')} onChange={e => setNotifTypes(t => e.target.checked ? [...t, 'fcm'] : t.filter(x => x !== 'fcm'))} />}
                label="FCM"
              />
              {/* Add more types as needed */}
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField label="Title" value={notifTitle} onChange={e => setNotifTitle(e.target.value)} fullWidth required sx={{ mb: 1 }} />
              <TextField label="Message" value={notifMsg} onChange={e => setNotifMsg(e.target.value)} fullWidth required multiline rows={2} sx={{ mb: 1 }} />
              <TextField label="Data" value={notifData} onChange={e => setNotifData(e.target.value)} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" onClick={handleSendNotif} disabled={!notifUser || notifTypes.length === 0 || !notifTitle || !notifMsg || notifLoading}>
                {notifLoading ? 'Sending...' : 'Send Notification'}
              </Button>
            </Grid>
            <Grid item xs={12}>
              {notifError && <Alert severity="error">{notifError}</Alert>}
              {notifResult && <Alert severity="success">Notification sent successfully: {JSON.stringify(notifResult)}</Alert>}
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Messages */}
      {message && (
        <Alert severity="success" sx={{ mt: 3 }}>
          {message}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      )}

      {/* Info Card */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Important Notes
          </Typography>
          <Typography variant="body2" paragraph>
            • <strong>Cost Control:</strong> API calls are expensive. Use sparingly and monitor usage.
          </Typography>
          <Typography variant="body2" paragraph>
            • <strong>No Updates:</strong> This only fetches missing data. Existing data is not updated.
          </Typography>
          <Typography variant="body2" paragraph>
            • <strong>Admin Only:</strong> This page is restricted to admin users only.
          </Typography>
          <Typography variant="body2" paragraph>
            • <strong>Rate Limiting:</strong> API providers have rate limits. Respect them.
          </Typography>
          <Typography variant="body2">
            • <strong>Cost Tracking:</strong> Monitor API usage and costs regularly.
          </Typography>
        </CardContent>
      </Card>

      {/* Coins with Unknown Blockchain Section */}
      <Box sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Coins with Unknown Blockchain</Typography>
          {loadingUnknown ? <CircularProgress /> : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Symbol</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Blockchain</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {unknownCoins.map(coin => (
                    <TableRow key={coin.symbol}>
                      <TableCell>{coin.symbol}</TableCell>
                      <TableCell>{coin.name}</TableCell>
                      <TableCell>
                        <TextField
                          value={editBlockchains[coin.symbol] || ''}
                          onChange={e => handleEditBlockchain(coin.symbol, e.target.value)}
                          size="small"
                          placeholder="Enter blockchain"
                        />
                      </TableCell>
                      <TableCell>
                        <Button variant="contained" size="small" onClick={() => handleSaveBlockchain(coin)} disabled={savingBlockchain[coin.symbol]}>
                          {savingBlockchain[coin.symbol] ? <CircularProgress size={20} /> : 'Save'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

             {/* Price History Processor Controls */}
       <Box sx={{ mt: 4, mb: 4 }}>
         <Paper sx={{ p: 3 }}>
           <Typography variant="h6" gutterBottom>
             Price History Processor (Global)
           </Typography>
           
           {/* Statistics */}
           {priceHistoryStats && (
             <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
               <Typography variant="subtitle2" gutterBottom>Statistics:</Typography>
               <Grid container spacing={2}>
                 <Grid item xs={6} md={3}>
                   <Typography variant="body2" color="textSecondary">Total Coins:</Typography>
                   <Typography variant="h6">{priceHistoryStats.totalCoins}</Typography>
                 </Grid>
                 <Grid item xs={6} md={3}>
                   <Typography variant="body2" color="textSecondary">With History:</Typography>
                   <Typography variant="h6">{priceHistoryStats.coinsWithHistory}</Typography>
                 </Grid>
                 <Grid item xs={6} md={3}>
                   <Typography variant="body2" color="textSecondary">Coverage:</Typography>
                   <Typography variant="h6">{priceHistoryStats.coverage}%</Typography>
                 </Grid>
                 <Grid item xs={6} md={3}>
                   <Typography variant="body2" color="textSecondary">Needing Updates:</Typography>
                   <Typography variant="h6" color={priceHistoryStats.coinsNeedingUpdates > 0 ? 'error' : 'success'}>
                     {priceHistoryStats.coinsNeedingUpdates}
                   </Typography>
                 </Grid>
               </Grid>
               {priceHistoryStats.latestUpdate && (
                 <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                   Last Update: {new Date(priceHistoryStats.latestUpdate).toLocaleString()}
                 </Typography>
               )}
             </Box>
           )}
           
           <Grid container spacing={2} alignItems="center">
             <Grid item xs={12} md={3}>
               <Button
                 variant="contained"
                 onClick={fetchPriceHistoryStatus}
                 disabled={priceHistoryLoading}
                 fullWidth
                 sx={{ height: 40 }}
               >
                 {priceHistoryLoading ? <CircularProgress size={20} /> : 'Check Status'}
               </Button>
             </Grid>
             <Grid item xs={12} md={3}>
               <Button
                 variant="contained"
                 onClick={triggerPriceHistoryProcess}
                 disabled={priceHistoryLoading}
                 fullWidth
                 sx={{ height: 40 }}
               >
                 {priceHistoryLoading ? <CircularProgress size={20} /> : 'Trigger Process'}
               </Button>
             </Grid>
             <Grid item xs={12} md={3}>
               <Button
                 variant="outlined"
                 onClick={fetchPriceHistoryStats}
                 disabled={priceHistoryLoading}
                 fullWidth
                 sx={{ height: 40 }}
               >
                 Refresh Stats
               </Button>
             </Grid>
             <Grid item xs={12} md={3}>
               <Typography variant="body2" color="textSecondary">
                 Status: {priceHistoryStatus?.isActive ? 'Running' : 'Stopped'}
               </Typography>
               {priceHistoryStatus?.lastRun && (
                 <Typography variant="body2" color="textSecondary">
                   Last Run: {new Date(priceHistoryStatus.lastRun).toLocaleString()}
                 </Typography>
               )}
             </Grid>
           </Grid>
           
           {priceHistoryResult && (
             <Alert severity="success" sx={{ mt: 2 }}>
               {priceHistoryResult}
             </Alert>
           )}
           {priceHistoryError && (
             <Alert severity="error" sx={{ mt: 2 }}>
               {priceHistoryError}
             </Alert>
           )}
         </Paper>
       </Box>

       {/* Detailed Coin History Table */}
       <Box sx={{ mt: 4, mb: 4 }}>
         <Paper sx={{ p: 3 }}>
           <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
             <Typography variant="h6" gutterBottom>
               Detailed Coin History Data
             </Typography>
             <Box sx={{ display: 'flex', gap: 1 }}>
               <Button
                 variant="contained"
                 color="warning"
                 onClick={fillIncompleteHistoricalData}
                 disabled={fillIncompleteLoading}
                 size="small"
               >
                 {fillIncompleteLoading ? <CircularProgress size={16} /> : 'Fill Missing Data'}
               </Button>
               <Button
                 variant="contained"
                 color="error"
                 onClick={fillNullPrices}
                 disabled={fillNullsLoading}
                 size="small"
               >
                 {fillNullsLoading ? <CircularProgress size={16} /> : 'Fill NULL Prices'}
               </Button>
               <Button
                 variant="outlined"
                 onClick={fetchDetailedCoinHistory}
                 disabled={detailedHistoryLoading}
                 size="small"
               >
                 {detailedHistoryLoading ? <CircularProgress size={16} /> : 'Refresh'}
               </Button>
             </Box>
           </Box>
           
           {fillIncompleteProgress && (
             <Alert severity="info" sx={{ mb: 2 }}>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                 <CircularProgress size={16} />
                 {fillIncompleteProgress}
               </Box>
             </Alert>
           )}
           
           {fillIncompleteResult && (
             <Alert severity="success" sx={{ mb: 2 }}>
               {fillIncompleteResult}
             </Alert>
           )}
           
           {fillNullsProgress && (
             <Alert severity="info" sx={{ mb: 2 }}>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                 <CircularProgress size={16} />
                 {fillNullsProgress}
               </Box>
             </Alert>
           )}
           
           {fillNullsResult && (
             <Alert severity="success" sx={{ mb: 2 }}>
               {fillNullsResult}
             </Alert>
           )}
           
           {detailedHistoryLoading ? (
             <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
               <CircularProgress />
             </Box>
           ) : detailedCoinHistory.length > 0 ? (
             <TableContainer>
               <Table size="small">
                 <TableHead>
                   <TableRow>
                     <TableCell>Symbol</TableCell>
                     <TableCell align="right">Current Price</TableCell>
                     <TableCell align="right">Days with History</TableCell>
                     <TableCell align="center">Date Range</TableCell>
                     <TableCell align="center">Min Date</TableCell>
                     <TableCell align="center">Max Date</TableCell>
                     <TableCell align="center">Status</TableCell>
                   </TableRow>
                 </TableHead>
                 <TableBody>
                   {detailedCoinHistory.map((coin) => (
                     <TableRow key={coin.symbol}>
                       <TableCell>
                         <Typography variant="body2" fontWeight="bold">
                           {coin.symbol}
                         </Typography>
                       </TableCell>
                       <TableCell align="right">
                         <Typography variant="body2">
                           ${coin.currentPrice.toLocaleString(undefined, { 
                             minimumFractionDigits: 2, 
                             maximumFractionDigits: 8 
                           })}
                         </Typography>
                       </TableCell>
                       <TableCell align="right">
                         <Typography 
                           variant="body2" 
                           color={coin.daysWithHistory > 0 ? 'success.main' : 'error.main'}
                           fontWeight="bold"
                         >
                           {coin.daysWithHistory}
                         </Typography>
                       </TableCell>
                       <TableCell align="center">
                         <Typography variant="body2">
                           {coin.dateRangeDays} days
                         </Typography>
                       </TableCell>
                       <TableCell align="center">
                         <Typography variant="body2" fontSize="0.8rem">
                           {coin.minDate ? new Date(coin.minDate).toLocaleDateString() : 'N/A'}
                         </Typography>
                       </TableCell>
                       <TableCell align="center">
                         <Typography variant="body2" fontSize="0.8rem">
                           {coin.maxDate ? new Date(coin.maxDate).toLocaleDateString() : 'N/A'}
                         </Typography>
                       </TableCell>
                       <TableCell align="center">
                         <Chip
                           label={coin.hasHistory ? 'Has Data' : 'No Data'}
                           color={coin.hasHistory ? 'success' : 'error'}
                           size="small"
                           variant="outlined"
                         />
                       </TableCell>
                     </TableRow>
                   ))}
                 </TableBody>
               </Table>
             </TableContainer>
           ) : (
             <Box sx={{ textAlign: 'center', p: 3 }}>
               <Typography color="textSecondary">No coin data available</Typography>
             </Box>
           )}
         </Paper>
       </Box>
    </Container>
  );
}

export default AdminPage; 